import React, { useState } from "react";
import { projects } from "../../mock";
import { ArrowUpRight } from "lucide-react";

const SectionLabel = ({ index, title }) => (
  <div className="flex items-center gap-3 mb-10">
    <span className="font-mono text-[11px] tracking-[0.22em] text-[#B8542B]">
      {index}
    </span>
    <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#6B5F52]">
      {title}
    </span>
    <span className="flex-1 h-px bg-[#E8E0D3]" />
  </div>
);

const kinds = ["All", ...Array.from(new Set(projects.map((p) => p.kind)))];

const Projects = () => {
  const [filter, setFilter] = useState("All");
  const list = filter === "All" ? projects : projects.filter((p) => p.kind === filter);

  return (
    <section id="projects" className="py-24 md:py-32 px-6 md:px-10">
      <div className="max-w-6xl mx-auto">
        <SectionLabel index="03" title="Selected projects" />

        <div className="grid md:grid-cols-12 gap-8 md:gap-10 mb-12">
          <h2 className="md:col-span-8 font-serif text-3xl md:text-5xl text-[#1F1B16] leading-[1.1] tracking-tight">
            Things I've built, shipped, and <span className="italic text-[#8a5a3d]">stress-tested</span>.
          </h2>
          <p className="md:col-span-4 text-[#6B5F52] text-base leading-relaxed md:pt-3">
            A curated cross-section — frameworks, platforms, and the occasional craft project. Numbers are real, names are paraphrased.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 mb-10">
          {kinds.map((k) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              className={`px-4 py-1.5 rounded-full text-xs font-mono uppercase tracking-[0.14em] transition-colors duration-200 border ${
                filter === k
                  ? "bg-[#1F1B16] text-[#FAF7F2] border-[#1F1B16]"
                  : "bg-transparent text-[#6B5F52] border-[#E8E0D3] hover:border-[#1F1B16] hover:text-[#1F1B16]"
              }`}
            >
              {k}
            </button>
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-6 md:gap-7">
          {list.map((p, i) => (
            <article
              key={p.id}
              className={`group relative p-7 md:p-9 bg-[#F5F0E8]/60 border border-[#E8E0D3] rounded-2xl hover:bg-[#F5F0E8] hover:border-[#1F1B16]/30 transition-colors duration-300 ${
                i % 3 === 0 ? "md:translate-y-2" : ""
              }`}
            >
              <div className="flex items-center justify-between mb-6">
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#B8542B]">
                  {p.kind}
                </span>
                <span className="font-mono text-[11px] text-[#8a7d6e]">{p.year}</span>
              </div>

              <h3 className="font-serif text-2xl md:text-[26px] text-[#1F1B16] leading-tight">
                {p.title}
              </h3>
              <div className="mt-1 text-[13px] text-[#8a5a3d] italic">{p.role}</div>

              <p className="mt-4 text-[#3a342d] text-[15px] leading-relaxed">
                {p.summary}
              </p>

              {p.metrics && p.metrics.length > 0 && (
                <div className="mt-6 grid grid-cols-2 gap-4">
                  {p.metrics.map((m) => (
                    <div
                      key={m.k}
                      className="border-t border-[#E8E0D3] pt-3"
                    >
                      <div className="font-serif text-2xl text-[#1F1B16] leading-none">
                        {m.v}
                      </div>
                      <div className="mt-1 text-[11px] text-[#6B5F52]">{m.k}</div>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-6 flex flex-wrap gap-1.5">
                {p.stack.map((s) => (
                  <span
                    key={s}
                    className="font-mono text-[10.5px] px-2 py-0.5 rounded border border-[#E8E0D3] text-[#6B5F52]"
                  >
                    {s}
                  </span>
                ))}
              </div>

              <div className="mt-7 flex items-center gap-2 text-[#1F1B16] text-sm font-medium">
                <span>Read case study</span>
                <ArrowUpRight
                  size={16}
                  className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;
